import { useState } from 'react';
import { Button, Input, InputNumber, Select, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MANUAL_ADJ_CONFIRM_MONTHS,
  MANUAL_ADJ_DESCRIPTION_MAX,
  addMonthKey,
  earliestStartMonth,
  formatAmountAbs,
  manualAdjustmentMonths,
  manualMonthIndex,
  monthDiff,
  monthKeyLabel,
  startMonthProblem,
  type ManualAdjustment,
  type ManualAdjustmentInput,
  type ManualAdjustmentKind,
  type ManualAdjustmentsResponse,
} from '@salaries/shared';
import {
  createManualAdjustment,
  deleteManualAdjustment,
  getManualAdjustments,
  updateManualAdjustment,
} from '../../api/hugim';
import { Money } from '../../components/Money';
import { confirmModal } from '../../components/confirmModal';

// תוספות וקיזוזים ידניים — amounts typed for this staff member outside the
// receipts channel (see @salaries/shared manual-adjustments). Unlike the rest of
// the drawer, every add / edit / delete saves on its own: each one can need its
// own confirmation (a long spread, a spread already under way), and each one
// is audited by itself.

const KIND_TEXT: Record<
  ManualAdjustmentKind,
  { title: string; help: string; amountLabel: string }
> = {
  addition: {
    title: 'תוספת לשכר',
    help: 'סכום שמתווסף לשכר החודשי של איש הצוות. התוספת אינה תלויה בקבלות ובאחוזים שהוגדרו לאיש הצוות.',
    amountLabel: 'שכר',
  },
  offset: {
    title: 'קיזוז מהשכר',
    help: 'סכום שיורד מהשכר החודשי של איש הצוות. מזינים סכום חיובי והמערכת מורידה אותו מהשכר. הקיזוז אינו תלוי בקבלות ואינו מושפע מביטולים או מזיכויים של לקוחות.',
    amountLabel: 'קיזוז מהשכר',
  },
};

const MONTHS_HELP =
  'בכמה חודשים הסכום ישולם. הסכום שהזנת הוא הסכום לכל חודש, לא סך הכל שמתחלק: 1,200 ש"ח עם 3 חודשים = 1,200 ש"ח בכל אחד משלושת החודשים, 3,600 ש"ח בסך הכל. 1 = החודש שנבחר בלבד.';

/** How many months ahead the "החל מחודש" picker offers. */
const PICKER_AHEAD = 36;

const formatIls = (n: number) => `${formatAmountAbs(n)} ש"ח`;

/** The first month at or after `from` that isn't closed. */
function firstOpenMonth(from: string, closed: string[]): string {
  let m = from;
  while (closed.includes(m)) m = addMonthKey(m, 1);
  return m;
}

/** Where a row stands against the open month: "חודש 2 מתוך 3", or that it
 *  hasn't started / is over. */
function progressText(row: ManualAdjustment, openMonth: string): string {
  const idx = manualMonthIndex(row, openMonth);
  if (idx != null) return `חודש ${idx} מתוך ${row.months}`;
  return monthDiff(openMonth, row.start_month) > 0
    ? `מתחיל ב${monthKeyLabel(row.start_month)}`
    : 'הסתיים';
}

interface Draft {
  description: string;
  amount: number | null;
  start_month: string;
  months: number | null;
}

/** Field-level problems; empty = valid. A negative amount is an error, never
 *  flipped to positive. */
function draftErrors(d: Draft): Partial<Record<keyof Draft, string>> {
  const e: Partial<Record<keyof Draft, string>> = {};
  if (!d.description.trim()) e.description = 'יש להזין תיאור';
  if (d.amount == null) e.amount = 'יש להזין סכום';
  else if (d.amount <= 0) e.amount = 'יש להזין סכום חיובי';
  else if (Math.abs(d.amount * 100 - Math.round(d.amount * 100)) > 1e-6) e.amount = 'עד שתי ספרות אחרי הנקודה';
  if (d.months == null || !Number.isInteger(d.months) || d.months < 1) e.months = 'מספר שלם, 1 ומעלה';
  return e;
}

export function ManualAdjustmentsFields({
  staffId,
  isSelf,
  tracked,
}: {
  staffId: number;
  isSelf: boolean;
  /** Out of payroll → the rows stay but pay nothing; say so. */
  tracked: boolean;
}) {
  const queryKey = ['manual-adjustments', staffId];
  const q = useQuery({
    queryKey,
    queryFn: () => getManualAdjustments(staffId),
    enabled: !isSelf,
  });

  return (
    <div>
      <div className="text-heading-3-bold text-main-text mb-1 mt-2">תוספות וקיזוזים ידניים</div>
      <div className="text-small text-secondary-text mb-3">
        כאן מזינים סכומים שמתווספים לשכר או יורדים ממנו, בלי קשר לרישום הלקוחות ולתשלומים שלהם. סכום
        שמוזן כאן נשאר כפי שהוא: ביטול, זיכוי או החזר ללקוח אינם משנים אותו. לשינוי או לביטול של סכום
        שהוזן כאן, יש לערוך או למחוק את השורה במסך הזה.
      </div>
      {/* This section saves row by row, unlike the rest of the drawer — say so,
          or a row typed here and left unsaved is lost on the drawer's save. */}
      <div className="text-small text-main-text mb-3">
        שורות בסקשן הזה נשמרות מיד בלחיצה על "הוסף" או "שמירה" שבשורה, ולא בכפתור השמירה של החלונית.
      </div>
      {!tracked && !isSelf && (
        <div className="text-small text-warning-yellow mb-3">
          איש הצוות לא כלול בחישוב השכר, ולכן התוספות והקיזוזים שלו לא ייכנסו לשכר עד שייכלל שוב.
        </div>
      )}
      {isSelf ? (
        <div className="text-small text-secondary-text mb-4">לא ניתן להזין תוספת או קיזוז לעצמך.</div>
      ) : q.isLoading || !q.data ? (
        <div className="text-small text-secondary-text mb-4">
          {q.isError ? 'שגיאה בטעינת התוספות והקיזוזים' : 'טוען...'}
        </div>
      ) : (
        <>
          <KindBlock kind="addition" staffId={staffId} data={q.data} queryKey={queryKey} />
          <KindBlock kind="offset" staffId={staffId} data={q.data} queryKey={queryKey} />
        </>
      )}
    </div>
  );
}

function KindBlock({
  kind,
  staffId,
  data,
  queryKey,
}: {
  kind: ManualAdjustmentKind;
  staffId: number;
  data: ManualAdjustmentsResponse;
  queryKey: unknown[];
}) {
  const queryClient = useQueryClient();
  const text = KIND_TEXT[kind];
  const rows = data.items.filter((r) => r.kind === kind); // entry order (ascending id)
  const defaultStart = firstOpenMonth(data.open_month, data.closed_months);
  const blank = (): Draft => ({ description: '', amount: null, start_month: defaultStart, months: 1 });
  const [draft, setDraft] = useState<Draft>(blank);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Draft | null>(null);

  const onSaved = (next: ManualAdjustmentsResponse) => {
    queryClient.setQueryData(queryKey, next);
    // The report and the drawer read these through the payroll computation.
    void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
    void queryClient.invalidateQueries({ queryKey: ['payroll-detail'] });
    toast.success('נשמר');
  };
  const onError = (e: unknown) => toast.error(e instanceof Error ? e.message : 'שגיאה בשמירה');

  const createM = useMutation({
    mutationFn: (input: ManualAdjustmentInput) => createManualAdjustment(staffId, input),
    onSuccess: (next) => {
      onSaved(next);
      setDraft(blank());
    },
    onError,
  });
  const updateM = useMutation({
    mutationFn: ({ id, input }: { id: number; input: Omit<ManualAdjustmentInput, 'kind'> }) =>
      updateManualAdjustment(id, input),
    onSuccess: (next) => {
      onSaved(next);
      setEditingId(null);
      setEditDraft(null);
    },
    onError,
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => deleteManualAdjustment(id),
    onSuccess: onSaved,
    onError,
  });

  /** The row's months that are already closed (frozen). */
  const closedOf = (row: Pick<ManualAdjustment, 'start_month' | 'months'>) =>
    manualAdjustmentMonths(row).filter((m) => data.closed_months.includes(m));

  /** Ask before saving when it is needed; run `save` once confirmed. */
  const confirmThen = (lines: string[], save: () => void) => {
    if (!lines.length) return save();
    confirmModal({
      title: 'לפני השמירה',
      content: (
        <div className="flex flex-col gap-2">
          {lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
      ),
      okText: 'שמירה',
      onOk: save,
    });
  };

  const longSpreadLine = (months: number) =>
    months > MANUAL_ADJ_CONFIRM_MONTHS
      ? [
          `${kind === 'addition' ? 'הסכום ישולם' : 'הסכום ירד מהשכר'} ב-${months} חודשים (יותר מ-${MANUAL_ADJ_CONFIRM_MONTHS}). לשמור?`,
        ]
      : [];

  const add = () => {
    if (Object.keys(draftErrors(draft)).length) return;
    const input: ManualAdjustmentInput = {
      kind,
      description: draft.description.trim(),
      amount: draft.amount!,
      start_month: draft.start_month,
      months: draft.months!,
    };
    confirmThen(longSpreadLine(input.months), () => createM.mutate(input));
  };

  const saveEdit = (row: ManualAdjustment) => {
    if (!editDraft || Object.keys(draftErrors(editDraft)).length) return;
    const input = {
      description: editDraft.description.trim(),
      amount: editDraft.amount!,
      start_month: editDraft.start_month,
      months: editDraft.months!,
    };
    const lines = [...longSpreadLine(input.months)];
    const frozen = closedOf(row);
    if (frozen.length) {
      lines.push(
        `${frozen.length === 1 ? 'חודש אחד מהפריסה כבר נסגר' : `${frozen.length} חודשים מהפריסה כבר נסגרו`} (${frozen
          .map(monthKeyLabel)
          .join(', ')}). הסכום בהם נשאר כפי ששולם.`,
        'השינוי יחול רק על החודשים שעוד לא נסגרו.',
      );
      if (input.months < row.months || input.start_month !== row.start_month) {
        const after = manualAdjustmentMonths(input);
        const dropped = manualAdjustmentMonths(row).filter(
          (m) => !after.includes(m) && !data.closed_months.includes(m),
        );
        if (dropped.length === 1) lines.push(`בחודש ${monthKeyLabel(dropped[0]!)} הסכום כבר לא ייכנס לשכר.`);
        else if (dropped.length) lines.push(`בחודשים ${dropped.map(monthKeyLabel).join(', ')} הסכום כבר לא ייכנס לשכר.`);
      }
    }
    confirmThen(lines, () => updateM.mutate({ id: row.id, input }));
  };

  const remove = (row: ManualAdjustment) => {
    const frozen = closedOf(row);
    confirmModal({
      title: kind === 'addition' ? 'מחיקת תוספת' : 'מחיקת קיזוז',
      content: frozen.length
        ? `החודשים שכבר נסגרו (${frozen.map(monthKeyLabel).join(', ')}) נשארים כפי ששולמו. השורה "${row.description}" תפסיק להיכנס לשכר מהחודש הפתוח ואילך.`
        : `למחוק את השורה "${row.description}"?`,
      okText: 'מחיקה',
      okButtonProps: { danger: true },
      onOk: () => deleteM.mutate(row.id),
    });
  };

  // From the month before the open month on, closed months left out. A spread
  // already under way keeps its own start month (closed or past) as a choice.
  const monthOptions = (keep?: string) => {
    const out: { value: string; label: string }[] = [];
    const first = earliestStartMonth(data.open_month);
    for (let i = 0; i <= PICKER_AHEAD; i++) {
      const m = addMonthKey(first, i);
      if (startMonthProblem(m, data.open_month, data.closed_months) == null) {
        out.push({ value: m, label: monthKeyLabel(m) });
      }
    }
    if (keep && !out.some((o) => o.value === keep)) {
      out.unshift({
        value: keep,
        label: data.closed_months.includes(keep) ? `${monthKeyLabel(keep)} (נסגר)` : monthKeyLabel(keep),
      });
    }
    return out;
  };

  return (
    <div className="mb-5">
      <div className="text-label font-medium text-main-text mb-1">{text.title}</div>
      <div className="text-small text-secondary-text mb-2">{text.help}</div>
      <EntryRow
        kind={kind}
        draft={draft}
        onChange={setDraft}
        monthOptions={monthOptions()}
        action={
          <Button
            disabled={!draft.description.trim() || draft.amount == null}
            loading={createM.isPending}
            onClick={add}
          >
            הוסף
          </Button>
        }
      />
      {rows.length > 0 && (
        <div className="border rounded mt-2">
          {rows.map((r) =>
            editingId === r.id && editDraft ? (
              <div key={r.id} className="px-3 py-2 border-b last:border-b-0">
                <EntryRow
                  kind={kind}
                  draft={editDraft}
                  onChange={setEditDraft}
                  monthOptions={monthOptions(r.start_month)}
                  showHelp={false}
                  action={
                    <span className="flex gap-1">
                      <Button type="primary" loading={updateM.isPending} onClick={() => saveEdit(r)}>
                        שמירה
                      </Button>
                      <Button
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft(null);
                        }}
                      >
                        ביטול
                      </Button>
                    </span>
                  }
                />
              </div>
            ) : (
              <div
                key={r.id}
                className="flex items-center justify-between gap-2 px-3 py-2 border-b last:border-b-0"
              >
                <span className="flex flex-col min-w-0">
                  {/* Long descriptions are cut to one line; the whole text on hover. */}
                  <Tooltip title={r.description} placement="topRight" mouseEnterDelay={0.4}>
                    <span className="text-main-text truncate">{r.description}</span>
                  </Tooltip>
                  <span className="text-small text-secondary-text">
                    {`סה"כ ${formatIls(r.amount * r.months)} על פני ${r.months} ${r.months === 1 ? 'חודש' : 'חודשים'}`}
                    {' · '}
                    {progressText(r, data.open_month)}
                  </span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {kind === 'offset' ? (
                    <span className="text-error-red">
                      -<Money value={r.amount} />
                    </span>
                  ) : (
                    <Money value={r.amount} />
                  )}
                  <Tooltip title="עריכה">
                  <Button
                    size="small"
                    type="text"
                    aria-label="עריכה"
                    icon={<FontAwesomeIcon icon={faPen} />}
                    onClick={() => {
                      setEditingId(r.id);
                      setEditDraft({
                        description: r.description,
                        amount: r.amount,
                        start_month: r.start_month,
                        months: r.months,
                      });
                    }}
                  />
                  </Tooltip>
                  <Tooltip title="מחיקה">
                  <Button
                    size="small"
                    type="text"
                    danger
                    aria-label="מחיקה"
                    icon={<FontAwesomeIcon icon={faTrashCan} />}
                    loading={deleteM.isPending && deleteM.variables === r.id}
                    onClick={() => remove(r)}
                  />
                  </Tooltip>
                </span>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/** The four fields + the row's action button(s). */
function EntryRow({
  kind,
  draft,
  onChange,
  monthOptions,
  action,
  showHelp = true,
}: {
  kind: ManualAdjustmentKind;
  draft: Draft;
  onChange: (next: Draft) => void;
  monthOptions: { value: string; label: string }[];
  action: React.ReactNode;
  /** The months help text — under the add row; the edit row leaves it out. */
  showHelp?: boolean;
}) {
  const errors = draftErrors(draft);
  // Show an amount/months error only once something was typed there.
  const amountErr = draft.amount != null ? errors.amount : undefined;
  const monthsErr = errors.months;
  return (
    <div>
      <div className="flex flex-wrap items-start gap-2">
        <Field label="תיאור" style={{ flex: '1 1 180px' }}>
          <Input
            value={draft.description}
            maxLength={MANUAL_ADJ_DESCRIPTION_MAX}
            onChange={(e) => onChange({ ...draft, description: e.target.value })}
          />
        </Field>
        <Field label={KIND_TEXT[kind].amountLabel} error={amountErr} style={{ width: 130 }}>
          <InputNumber
            dir="ltr"
            style={{ width: '100%' }}
            status={amountErr ? 'error' : undefined}
            prefix={kind === 'offset' ? '-' : undefined}
            value={draft.amount ?? undefined}
            onChange={(v) => onChange({ ...draft, amount: v == null ? null : Number(v) })}
          />
        </Field>
        <Field label="החל מחודש" style={{ width: 150 }}>
          <Select
            value={draft.start_month}
            options={monthOptions}
            onChange={(v) => onChange({ ...draft, start_month: v })}
          />
        </Field>
        <Field label="חודשי פריסה" error={monthsErr} style={{ width: 100 }}>
          <InputNumber
            dir="ltr"
            style={{ width: '100%' }}
            status={monthsErr ? 'error' : undefined}
            value={draft.months ?? undefined}
            onChange={(v) => onChange({ ...draft, months: v == null ? null : Number(v) })}
          />
        </Field>
        <div className="flex flex-col">
          <div className="text-small mb-1">&nbsp;</div>
          {action}
        </div>
      </div>
      {showHelp && <div className="text-small text-secondary-text mt-1">{MONTHS_HELP}</div>}
    </div>
  );
}

function Field({
  label,
  error,
  style,
  children,
}: {
  label: string;
  error?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col" style={style}>
      <div className="text-small text-main-text mb-1">{label}</div>
      {children}
      {error && <div className="text-small text-error-red mt-0.5">{error}</div>}
    </div>
  );
}
